import { cn } from "@faire/prototype-playground/lib/utils";
import { tsv } from "@faire/slate/lib/tsv";

import { FreeShippingBadge, PlusIconDark } from "./ProductTilesSearchTile";
import { SaveHeartButton } from "./SaveHeartButton";

interface MinimizedProductTileProps {
  imageUrl: string;
  productName: string;
  price: string;
  brandName: string;
  freeShipping?: boolean;
  isFavorited?: boolean;
  onFavorite?: () => void;
  onClick?: () => void;
  className?: string;
  /** Adds an always-visible quick-add control at the bottom-right of the image. */
  showQuickAdd?: boolean;
  onQuickAdd?: () => void;
  /**
   * Hides the favorite until the tile is hovered. Favorited tiles keep it
   * visible either way, so a save still reads at rest.
   */
  hoverOnlyHeart?: boolean;
}

export function MinimizedProductTile({
  imageUrl,
  productName,
  price,
  brandName,
  freeShipping = false,
  isFavorited = false,
  onFavorite,
  onClick,
  className,
  showQuickAdd = false,
  onQuickAdd,
  hoverOnlyHeart = false,
}: MinimizedProductTileProps) {
  return (
    <div
      className={cn("group flex cursor-pointer flex-col gap-3", className)}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-[4px]">
        <img
          src={imageUrl}
          alt={productName}
          className="absolute inset-0 size-full rounded-[4px] object-cover"
        />
        <div className="pointer-events-none absolute inset-0 rounded-[4px] bg-(--slate-color-grey-1000)/[0.02]" />
        <SaveHeartButton
          ariaLabel={isFavorited ? "Un-follow" : "Follow"}
          className="absolute top-2 right-2"
          hoverOnly={hoverOnlyHeart}
          isSaved={isFavorited}
          onToggle={() => onFavorite?.()}
          size="small"
        />

        {showQuickAdd ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onQuickAdd?.();
            }}
            aria-label="Add to cart"
            className={quickAddStyle.cls()}
          >
            <PlusIconDark />
          </button>
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5">
        <div className="type-fs-paragraph text-fs-text-primary flex items-start gap-1 whitespace-nowrap">
          <span className="font-medium">{price}</span>
          <span className="min-w-0 flex-1 overflow-hidden text-ellipsis">
            {productName}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="type-fs-paragraph text-fs-text-primary truncate underline">
            {brandName}
          </span>
        </div>
        {freeShipping ? (
          <div className="mt-[2px]">
            <FreeShippingBadge label="Free shipping" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Smaller sibling of the grid tile's quick add, sized for the condensed tile. */
const quickAddStyle = tsv({
  base: [
    "absolute right-2 bottom-2 flex size-7 cursor-pointer items-center",
    "border-fs-border-muted bg-fs-surface-primary justify-center rounded-full border",
    "shadow-[0px_0px_1px_0px_rgba(0,0,0,0.15)] hover:shadow-[0_0_0_1px_#222]",
  ],
});
